import React, { CSSProperties } from 'react';
import AssetsFull from '@fabric-ui/icons/svg/apps/AssetsFull.svg';
import FindingsFull from '@fabric-ui/icons/svg/apps/FindingsFull.svg';
import PolicyViolation from '@fabric-ui/icons/svg/colorful/Policy_Violation.svg';
import { Box } from '@mui/material';
import {
  EditFactorType,
  EntitiesFactor,
  EntityBuildingBlock,
  EntityWeight,
  GetRiskScoringConfigResponse,
  KeyFactor,
  Factor as RiskFactor,
  ScoringFilter,
  ScoringRule,
} from './types';

export type RiskScoreViewModel = {
  entityWeights: EntityWeight[];
  buildingBlocks: EntityBuildingBlock[];
};

/**
 * Transforms the raw `getRiskScoringConfig` API response into UI view models.
 *
 * Strategy:
 * - `entityWeights` — built from `riskScoreProjectionToBaseScoresMap`, using
 *   `projectionToRiskScores` to resolve current weight/aggType per base score.
 * - `buildingBlocks` — built from `projectionToBaseScores`, extracting all
 *   unique factor fieldNames across all configTypes and factorGroups.
 */
export const transformRiskScoringConfig = (data: GetRiskScoringConfigResponse, riskScoreProjectionName: string): RiskScoreViewModel => {
  const { projectionToRiskScores, projectionToBaseScores, riskScoreProjectionToBaseScoresMap } = data;

  // Index base score data by projectionName for O(1) lookups
  const baseScoreByProjection = Object.fromEntries(
    projectionToBaseScores.map(({ projectionName, baseScore }) => [projectionName, baseScore])
  );

  // Index weight contributions by base score projectionName for the target risk score
  const riskScoreEntry = projectionToRiskScores.find(e => e.projectionName === riskScoreProjectionName);
  const weightContributionByProjection = Object.fromEntries(
    (riskScoreEntry?.riskScore.config.baseScoreWeightContribution ?? []).map(entry => [entry.projectionName, entry])
  );

  // Find the allowed base scores for this risk score projection
  const mapEntry = riskScoreProjectionToBaseScoresMap.find(e => e.riskScoreProjectionName === riskScoreProjectionName);
  const allowedBaseScores = mapEntry?.baseScores ?? [];

  // ── entityWeights ────────────────────────────────────────────────────────────
  // Ordered by the map — every allowed base score becomes a weight row
  const entityWeights: EntityWeight[] = allowedBaseScores.map(({ projectionName, allowedAggTypes }) => {
    const contribution = weightContributionByProjection[projectionName];
    const baseScore = baseScoreByProjection[projectionName];
    const factorCount = countFactors(baseScore?.config.configTypes ?? []);
    const hasAggregation = allowedAggTypes.length > 0;

    return {
      projectionId: projectionName,
      name: formatProjectionName(projectionName),
      weight: contribution ? Math.round(contribution.weight * 100) : 0,
      factorCount,
      hasAggregation,
      allowedAggTypes,
      aggregation: contribution?.aggType ?? (hasAggregation ? allowedAggTypes[0] : undefined),
    };
  });

  // ── buildingBlocks ───────────────────────────────────────────────────────────
  // One block per base score in projectionToBaseScores
  const buildingBlocks: EntityBuildingBlock[] = projectionToBaseScores.map(({ projectionName, baseScore }) => {
    const factors = extractFactors(baseScore.config.configTypes ?? []);

    return {
      projectionId: projectionName,
      name: formatProjectionName(projectionName),
      activeCount: factors.length,
      activeLabel: 'Active Factors',
      factors,
      configTypes: baseScore.config.configTypes ?? [],
    };
  });

  return { entityWeights, buildingBlocks };
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Extracts unique factors as KeyFactor[] from all configTypes and factorGroups */
const extractFactors = (
  configTypes: GetRiskScoringConfigResponse['projectionToBaseScores'][0]['baseScore']['config']['configTypes']
): KeyFactor[] => {
  const allFieldNames = configTypes
    .flatMap(ct => ct.factorGroups)
    .flatMap(fg => fg.factors)
    .map(f => f.fieldName);

  return [...new Set(allFieldNames)].map(name => ({ name }));
};

/** Counts all factors across all configTypes and factorGroups */
const countFactors = (
  configTypes: GetRiskScoringConfigResponse['projectionToBaseScores'][0]['baseScore']['config']['configTypes']
): number => configTypes.reduce((total, ct) => total + ct.factorGroups.reduce((g, fg) => g + fg.factors.length, 0), 0);

/** Converts a projection name like "uber_assets" → "Assets" */
export const formatProjectionName = (projectionName: string): string =>
  projectionName
    .replace(/^uber_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

export const getProjToWeightUiConfigMap = ({ iconStyle = { width: 26, height: 26 } }: { iconStyle?: CSSProperties } = {}) => ({
  uber_assets: {
    sliderColor: 'var(--dataviz-color-categorical-primary-chart16)',
    iconImg: <img src={AssetsFull} alt="ASSETS" style={{ ...iconStyle }} />,
  },
  uber_findings: {
    sliderColor: 'var(--dataviz-color-categorical-primary-chart04)',
    iconImg: <img src={FindingsFull} alt="FINDINGS" style={{ ...iconStyle }} />,
  },
  uber_policy_violations: {
    sliderColor: 'var(--dataviz-color-categorical-primary-chart18)',
    iconImg: <img src={PolicyViolation} alt="POLICY VIOLATIONS" style={{ ...iconStyle }} />,
  },
});

export const BaseScoreTitleOverride = {
  uber_assets: 'Asset Business Context',
  uber_policy_violations: 'Policy Violation Severity',
};

export const riskFactorToEditFactor = (factor: RiskFactor, fieldMap: Record<string, { displayName: string }>): EditFactorType => ({
  name: fieldMap[factor.fieldName]?.displayName ?? factor.fieldName,
  fieldName: factor.fieldName,
  fallBackScore: factor.fallbackScore,
  scoringFilters: factor.scoringRules as unknown as ScoringFilter[],
  minPoints: 0,
  maxPoints: 0,
});

export const editFactorToRiskFactor = (factor: EditFactorType): RiskFactor => ({
  fieldName: factor.fieldName!,
  fallbackScore: factor.fallBackScore,
  scoringRules: factor.scoringFilters as unknown as ScoringRule[],
});

export const toEntitiesFactor = (projectionId: string, factors: EditFactorType[]): EntitiesFactor => ({
  projectionId: { name: projectionId, builtIn: false },
  minPoints: 0,
  maxPoints: 0,
  factors,
});

const untitledRegex = /^untitled\s\d+$/i;

const getEmptyFactorName = (entitiesFactor: EntitiesFactor): string => {
  const factorNames = entitiesFactor.factors.map(f => f.name);
  const lastUntitled = factorNames.findLast(name => untitledRegex.test(name.toLowerCase()));
  if (!lastUntitled) return 'Untitled 1';
  const [header, index] = lastUntitled.split(' ');
  return `${header} ${Number(index) + 1}`;
};

export const emptyEditFactor = (entitiesFactor: EntitiesFactor): EditFactorType => ({
  added: true,
  fallBackScore: 0,
  fieldName: undefined,
  maxPoints: 0,
  minPoints: 0,
  name: getEmptyFactorName(entitiesFactor),
  scoringFilters: [],
});

export { getScoreCondition, emptyFilter } from '../../components/scoring/utils';

export const numberInputProps = {
  actionIcons: [<Box sx={{ color: 'var(--color-content-base-primary)' }}>%</Box>],
  size: 'small',
  disableClearButton: true,
  type: 'number',
  style: { width: 100 },
};
